import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { ProjectDto } from '@werft/contracts';

/**
 * Was die angemeldete Person in diesem Projekt darf (P15). Der Server prüft alles selbst; die Oberfläche bietet nur
 * an, was gelingen kann. Gäste und Mitglieder mit Zugangsprofil: Ansehen / Kommentieren / Ausführen / Bearbeiten –
 * Gäste nie löschen, einstellen oder einladen.
 */
export interface Rights {
  guest: boolean;
  lead: boolean;
  /** Struktur und Einstellungen (Listen, Abschnitte, Labels, Bereiche, Werkzeuge) */
  manage: boolean;
  /** Aufgaben, Dateien, Seiten bearbeiten */
  edit: boolean;
  /** ADR 0063: Aufgaben erledigen/wieder öffnen, Status setzen, Checklisten abhaken (Stufe „Ausführen“ und mehr) */
  execute: boolean;
  /** Kommentare, Antworten, Chat, Reaktionen */
  comment: boolean;
  /** Löschen und Papierkorb */
  remove: boolean;
}

export function rightsOf(p: Pick<ProjectDto, 'role' | 'guest' | 'preview' | 'limits'> & { archived?: boolean } | null | undefined): Rights {
  if (!p) return FULL;
  // ADR 0122: archiviert = nur lesen für alle (die Leitung stellt über den Hinweis wieder her)
  if (p.archived) return { guest: p.role === 'guest', lead: p.role === 'lead', manage: false, edit: false, execute: false, comment: false, remove: false };
  // P21: Vorschau „Als Gast ansehen“ – nur lesen
  if (p.preview) return { guest: true, lead: false, manage: false, edit: false, execute: false, comment: false, remove: false };
  if (p.role !== 'guest') {
    // ADR 0063: Mitglied mit Zugangsprofil – Stufe wie bei Gästen, Verwaltung nur mit „Bearbeiten“
    const stage = p.role === 'member' ? p.limits?.access : undefined;
    if (!stage || stage === 'edit') return { ...FULL, lead: p.role === 'lead' };
    return { guest: false, lead: false, manage: false, edit: false, execute: stage === 'execute', comment: stage !== 'view', remove: false };
  }
  const level = p.guest?.access ?? 'view';
  return { guest: true, lead: false, manage: false, edit: level === 'edit', execute: level === 'edit' || level === 'execute', comment: level !== 'view', remove: false };
}

const FULL: Rights = { guest: false, lead: false, manage: true, edit: true, execute: true, comment: true, remove: true };
const Ctx = createContext<Rights>(FULL);

export function RightsProvider({ project, children }: { project: Pick<ProjectDto, 'role' | 'guest' | 'preview' | 'limits' | 'archived'>; children: ReactNode }) {
  // Stabiler Wert: sonst zeichnete jedes Neuzeichnen der Projektseite alle Zeilen neu (Messbericht P5)
  const { role, guest, preview, limits, archived } = project;
  const value = useMemo(() => rightsOf({ role, guest, preview, limits, archived }), [role, guest?.access, guest?.wholeProject, preview, limits?.access, archived]); // eslint-disable-line react-hooks/exhaustive-deps
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useRights = () => useContext(Ctx);
